import { useEffect, useState } from "react";

interface NlmSearchConfig<TResponse, TOption> {
  endpoint: string;
  params?: string;
  parse: (data: TResponse) => TOption[];
}

export function useNlmSearch<TResponse, TOption>(
  query: string,
  { endpoint, params, parse }: NlmSearchConfig<TResponse, TOption>,
) {
  const [result, setResult] = useState<{ query: string; options: TOption[] } | null>(null);
  const canSearch = query.length >= 2;

  useEffect(() => {
    setResult(null);
    if (query.length < 2) return;

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const url = `${endpoint}?${params ? `${params}&` : ""}terms=${encodeURIComponent(query)}&maxList=20`;
        const response = await fetch(url, { signal: controller.signal });
        const options = parse(await response.json());
        if (!controller.signal.aborted) setResult({ query, options });
      } catch {
        if (!controller.signal.aborted) setResult({ query, options: [] });
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, endpoint, params, parse]);

  // Never expose a previous query's results while its replacement is debouncing.
  const current = canSearch && result?.query === query ? result : null;
  return { options: current?.options ?? [], isLoading: canSearch && !current };
}
